import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import Spinner from '@components/Spinner';
import AddTopicForm from '@components/pages/Settings/UserProfile/EditTopicsSettings/AddTopicForm';
import TopicList from '@components/pages/Settings/UserProfile/EditTopicsSettings/TopicList';
import useEventTracking from '@hooks/useEventTracking';
import useFollowedEntities from '@hooks/useFollowedEntities';
import { impression } from '@tracking';
import type { User } from '@utils/backend-api/currentUser';

import MyTISection from './Section';
import TrendingTopicPills from './TrendingTopicPills';
import { MY_TI_ENGAGEMENT_LABELS, MY_TI_SECTION_IDS } from './constants';
import useRegisterSection from './useRegisterSection';

const MyTITopics = ({ initialCurrentUser }: { initialCurrentUser?: User }) => {
  const { trackEvent } = useEventTracking();
  const identityId = initialCurrentUser?.identity_id;
  const { followedEntities, areFollowedIdentitiesLoading } = useFollowedEntities(identityId);

  const topicsDisplayText = useMemo(() => {
    if (!followedEntities) return MY_TI_ENGAGEMENT_LABELS.NotEngaged.Topics;
    const hasFollowedEntities = followedEntities?.length > 0;

    return hasFollowedEntities
      ? MY_TI_ENGAGEMENT_LABELS.Engaged.Topics
      : MY_TI_ENGAGEMENT_LABELS.NotEngaged.Topics;
  }, [followedEntities]);

  useRegisterSection(MY_TI_SECTION_IDS.Topics, !areFollowedIdentitiesLoading);

  if (areFollowedIdentitiesLoading) return <Spinner marginY="small" />;

  return (
    <MyTISection
      id={MY_TI_SECTION_IDS.Topics}
      highlight={topicsDisplayText.label}
      subtitle={topicsDisplayText.subtitle}
      title={topicsDisplayText.title}
    >
      <InView
        onChange={(inView) => {
          inView && trackEvent(impression({ impressionElement: MY_TI_SECTION_IDS.Topics }));
        }}
        triggerOnce
      >
        <div className="mt-32 flex flex-col gap-32 sm:flex-row md:gap-[156px]">
          <div className="flex flex-1 flex-col gap-32 border-0 border-solid border-grayscale-gray sm:border-r sm:pr-32 md:pr-[156px]">
            <div className="relative flex w-full flex-col gap-16">
              <h3 className="m-0 font-suisse-intl text-body-lg font-normal text-black">
                Trending topics / Search for topics
              </h3>
              <AddTopicForm className="w-full" identityId={identityId} />
            </div>
            <TrendingTopicPills identityId={identityId} />
          </div>
          <div className="flex flex-1 flex-col gap-24">
            <h3 className="m-0 font-suisse-intl text-body-lg font-normal text-black">Following</h3>
            <div className="flex flex-wrap">
              <TopicList identityId={identityId} />
            </div>
          </div>
        </div>
      </InView>
    </MyTISection>
  );
};

export default MyTITopics;
